"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Play, ChevronLeft } from "lucide-react";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import Image from "next/image";

interface WelcomeTourModalProps {
  isOpen: boolean;
  userName?: string;
  onStartTour: () => void;
  onSkip: () => void;
  /** Interactive step tours available? When false the modal is welcome + demo video only. */
  toursEnabled?: boolean;
  /** Open straight on the video (Settings → "Watch the demo"). */
  initialVideo?: boolean;
}

// Full product demo walkthrough — embedded so users can watch instead of (or
// before) running the interactive tour. youtube-nocookie + rel=0 keeps it clean.
const DEMO_VIDEO_ID = "3AFy47rWac0";

export function WelcomeTourModal({
  isOpen,
  userName,
  onStartTour,
  onSkip,
  toursEnabled = true,
  initialVideo = false,
}: WelcomeTourModalProps) {
  const { branding } = useBranding();
  const [showVideo, setShowVideo] = useState(initialVideo);
  if (!isOpen) return null;

  const firstName = userName?.split(" ")[0] || "there";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[9998]"
            onClick={onSkip}
          />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full px-4 transition-[max-width] duration-300 ${
              showVideo ? "max-w-2xl" : "max-w-sm"
            }`}
          >
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
              {/* Close */}
              <button
                onClick={onSkip}
                className="absolute top-3.5 right-3.5 p-1 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all z-10"
              >
                <X className="w-4 h-4" />
              </button>

              {showVideo ? (
                /* ── Demo video ── */
                <div className="px-6 pt-8 pb-5">
                  <button
                    onClick={() => setShowVideo(false)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-3"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                  <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                    Full demo walkthrough
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                    Watch the complete tour, then jump in.
                  </p>
                  <div className="mt-4 aspect-video w-full overflow-hidden rounded-xl bg-black">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}?rel=0&modestbranding=1`}
                      title="InsuraCentral demo walkthrough"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                      allowFullScreen
                    />
                  </div>
                </div>
              ) : (
                /* ── Welcome ── */
                <div className="px-7 pt-8 pb-6">
                  {/* Logo */}
                  <Image src="/favicon.png" alt="" width={40} height={40} className="rounded-xl mb-5" />

                  <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                    Hey {firstName}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                    {toursEnabled
                      ? "Quick walkthrough to get you set up — or watch the full demo. Takes about 2 minutes."
                      : "Two-minute demo of the whole platform, then you're in."}
                  </p>

                  {/* Features */}
                  <div className="mt-5 space-y-1.5">
                    {[
                      "Dashboard, analytics & agency management",
                      "Leads, pipelines & power dialer",
                      "Leaderboard, appointments & reminders",
                      "Fax, resources & settings",
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2.5">
                        <div className="w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                        <span className="text-sm text-gray-500">{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* Watch demo — secondary while the walkthrough exists; with tours off the primary button IS the demo */}
                  {toursEnabled && (
                  <button
                    onClick={() => setShowVideo(true)}
                    className="mt-5 flex items-center gap-2.5 w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-left hover:border-gray-300 hover:bg-gray-50 transition-all group"
                  >
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-500 shrink-0 group-hover:bg-red-100 transition-colors">
                      <Play className="w-3.5 h-3.5 fill-current" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Watch the full demo</span>
                      <span className="block text-xs text-gray-400">Prefer to watch first? See it in action</span>
                    </span>
                  </button>
                  )}
                </div>
              )}

              {/* Actions */}
              {showVideo ? (
                <div className="px-6 pb-6 flex items-center justify-end">
                  <button
                    onClick={onStartTour}
                    className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-gray-800 transition-all"
                  >
                    {toursEnabled ? "Start the walkthrough" : "Jump in"}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="px-7 pb-7 flex items-center justify-between">
                  <button
                    onClick={onSkip}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {toursEnabled ? "Skip" : "Skip, I'll explore"}
                  </button>
                  <button
                    onClick={toursEnabled ? onStartTour : () => setShowVideo(true)}
                    className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-gray-800 transition-all"
                  >
                    {toursEnabled ? "Get started" : "Watch the demo"}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default WelcomeTourModal;
